import { useState } from "react";
import { Share, SquarePlus, MoreVertical, Smartphone, Check, Download } from "lucide-react";
import { toast } from "sonner";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-7 h-7 border border-neon text-neon text-xs font-semibold flex items-center justify-center">{n}</span>
    <span className="text-sm text-neutral-300 leading-relaxed">
      {Icon && <Icon size={14} className="inline-block mr-1.5 -mt-0.5 text-neutral-400" aria-hidden="true" />}
      {children}
    </span>
  </li>
);

const InstallSteps = ({ ios, iosSafari }) => {
  if (ios) {
    return (
      <ol className="space-y-3" data-testid="install-steps-ios">
        {!iosSafari && <Step n="1" icon={Smartphone}>Open this page in <span className="text-white">Safari</span> (installing only works from Safari on iPhone &amp; iPad).</Step>}
        <Step n={iosSafari ? "1" : "2"} icon={Share}>Tap the <span className="text-white">Share</span> button — the square with an arrow at the bottom of Safari.</Step>
        <Step n={iosSafari ? "2" : "3"} icon={SquarePlus}>Scroll down and tap <span className="text-white">Add to Home Screen</span>.</Step>
        <Step n={iosSafari ? "3" : "4"} icon={Check}>Tap <span className="text-white">Add</span>. Sculpt Fusion now lives on your home screen.</Step>
      </ol>
    );
  }
  return (
    <ol className="space-y-3" data-testid="install-steps-android">
      <Step n="1" icon={MoreVertical}>Tap the <span className="text-white">⋮ menu</span> in the top-right corner of your browser.</Step>
      <Step n="2" icon={SquarePlus}>Tap <span className="text-white">Add to Home screen</span> (or <span className="text-white">Install app</span>).</Step>
      <Step n="3" icon={Check}>Confirm — the Sculpt Fusion icon appears on your home screen.</Step>
    </ol>
  );
};

// Button that installs the app (native prompt where supported) or shows step-by-step instructions.
export const InstallButton = ({ className = "", label = "Add to Home Screen", compact = false }) => {
  const { installed, canPrompt, ios, iosSafari, promptInstall } = useInstallPrompt();
  const [open, setOpen] = useState(false);

  if (installed) {
    return (
      <span data-testid="install-installed" className={`inline-flex items-center gap-2 text-neon text-xs uppercase tracking-[0.2em] ${className}`}>
        <Check size={14} /> Installed on this device
      </span>
    );
  }

  const onClick = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") toast.success("Sculpt Fusion added to your home screen!");
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <button type="button" onClick={onClick} data-testid="install-app-btn"
        className={compact
          ? `inline-flex items-center gap-2 border border-neon/60 text-neon text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 hover:bg-neon hover:text-black transition-colors touch-manipulation ${className}`
          : `inline-flex items-center gap-3 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-white transition-colors neon-glow touch-manipulation ${className}`}>
        <Download size={compact ? 14 : 18} aria-hidden="true" /> {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="install-dialog" className="bg-[#0A0A0A] border-neutral-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-3xl flex items-center gap-3">
              <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-lg" /> Add to Home Screen
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              One tap to your schedule, mats and alerts — no app store needed.
            </DialogDescription>
          </DialogHeader>
          <InstallSteps ios={ios} iosSafari={iosSafari} />
          <button type="button" onClick={() => setOpen(false)} data-testid="install-dialog-done"
            className="mt-2 w-full border border-neutral-800 text-neutral-300 text-xs uppercase tracking-[0.2em] py-3.5 hover:border-neon hover:text-neon transition-colors">
            Got it
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
